import React from "react";
import { Droplet } from "lucide-react";
import { Reveal } from "./Reveal";
import { getIcon } from "./icons";
import { useSiteContent } from "../../context/SiteContent";

export const OMSection = ({ onProposal }) => {
  const { content } = useSiteContent();
  const om = content?.omServices || {};
  const pillars = (om.pillars || []).filter((p) => p.isActive);
  const w = om.waterStandards || {};
  const limits = [
    ["Electrical Conductivity", w.conductivity],
    ["Total Dissolved Solids (TDS)", w.tds],
    ["pH Level", w.ph],
    ["Total Hardness", w.hardness],
    ["Turbidity", w.turbidity],
  ];

  return (
    <section id="om" className="relative py-24 md:py-32 bg-helios-slate overflow-hidden" data-testid="om-section">
      <div className="noise-overlay" />
      <div className="relative max-w-7xl mx-auto px-4 md:px-8">
        <Reveal className="max-w-3xl mb-14">
          <p className="overline text-helios-amber mb-3">Solar O&amp;M · Preventative Engineering</p>
          <h2 className="font-display text-4xl md:text-5xl font-extrabold tracking-tight text-white leading-[1.05]">
            Comprehensive Solar Plant O&amp;M &amp; Preventative Engineering
          </h2>
          <p className="mt-5 text-slate-300 text-lg leading-relaxed">
            Maximize plant uptime and preserve generation yield with scheduled robotic cleaning, thermal
            diagnostics, and high-voltage substation maintenance.
          </p>
        </Reveal>

        <div className="grid md:grid-cols-2 gap-5">
          {pillars.map((p, i) => {
            const Icon = getIcon(p.iconName);
            return (
              <Reveal key={p.id} delay={i * 0.08}>
                <div className="card-hover h-full rounded-2xl border border-white/10 bg-white/[0.04] p-7" data-testid={`om-pillar-${i}`}>
                  <div className="flex items-center justify-between mb-5">
                    <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-helios-cyan/15 text-helios-cyan"><Icon size={22} /></div>
                    <span className="font-mono text-[10px] uppercase tracking-widest text-helios-amber/80">{p.frequency}</span>
                  </div>
                  <h3 className="font-display text-xl font-bold text-white">{p.title}</h3>
                  <p className="text-slate-300 mt-2.5 leading-relaxed text-[15px]">{p.description}</p>
                </div>
              </Reveal>
            );
          })}
        </div>

        <Reveal delay={0.1} className="mt-6">
          <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-7 md:p-9" data-testid="om-water-card">
            <div className="flex items-center gap-3 mb-6">
              <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-helios-amber/15 text-helios-amber shrink-0"><Droplet size={22} /></div>
              <div>
                <h3 className="font-display text-xl font-bold text-white">Water Quality Compliance</h3>
                <p className="text-sm text-slate-400">Assured limits for hybrid wet-cleaning operations</p>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-px rounded-xl overflow-hidden bg-white/10">
              {limits.map(([k, v]) => (
                <div key={k} className="bg-helios-slate p-4">
                  <p className="font-mono text-base md:text-lg font-bold text-helios-cyan">{v}</p>
                  <p className="text-[11px] text-slate-400 mt-1.5 leading-snug">{k}</p>
                </div>
              ))}
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.15} className="mt-10">
          <button onClick={onProposal}
            className="btn-sheen inline-flex items-center gap-2 rounded-full bg-white px-7 py-4 text-sm font-semibold text-helios-slate hover:bg-helios-mist transition-colors"
            data-testid="om-proposal-cta">
            Request Custom O&amp;M Proposal
          </button>
        </Reveal>
      </div>
    </section>
  );
};
